import { CloseOutlined, SaveOutlined } from "@ant-design/icons";
import { Button, Col, Drawer, Flex, Form, Input, notification, Row, Select, Spin, Switch } from "antd";
import { useCreateInvoiceNameMutation, useUpdateInvoiceNameMutation } from "api/sales-order/invoice-name-api";
import useLoading from "hooks/useLoading";
import { GenderChicken } from "pages/sales-order/components/SalesOrderModalV2";
import { useEffect } from "react";
import { InvoiceName, InvoiceNameForm } from "../types";

interface InvoiceNameModalProps {
	open: boolean;
	onClose: () => void;
	invoiceName?: InvoiceName;
}

const InvoiceNameModal = ({ open, onClose, invoiceName }: InvoiceNameModalProps) => {
	const { isLoading: isLoadingMutations } = useLoading("mutations");

	const [form] = Form.useForm<InvoiceNameForm>();

	const [createInvoiceName] = useCreateInvoiceNameMutation();
	const [updateInvoiceName] = useUpdateInvoiceNameMutation();

	useEffect(() => {
		if (!open) {
			form.resetFields();
			return;
		}

		if (invoiceName) {
			form.setFieldsValue({ ...invoiceName, deliveryNoteName: invoiceName.deliveryNoteName ?? undefined });
		}
	}, [open, invoiceName, form]);

	const handleOnFinish = async (values: InvoiceNameForm) => {
		try {
			if (!invoiceName) {
				await createInvoiceName(values).unwrap();
			} else {
				await updateInvoiceName({ ...values, id: invoiceName.id }).unwrap();
			}

			onClose();
			notification.success({
				message: `${!invoiceName ? "Tạo mới" : "Cập nhật"} Invoice Name thành công!`,
			});
		} catch (error: any) {
			notification.error({
				message: error?.data?.message || "Có lỗi xảy ra.",
			});
		}
	};

	return (
		<Drawer
			title={`${!invoiceName ? "Tạo mới" : "Cập nhật"} Invoice Name`}
			open={open}
			width={500}
			onClose={onClose}
			footer={
				<Flex gap={10} justify="flex-end">
					<Button disabled={isLoadingMutations} onClick={onClose} icon={<CloseOutlined />}>
						Huỷ bỏ
					</Button>
					<Button loading={isLoadingMutations} disabled={isLoadingMutations} type="primary" onClick={() => form.submit()} ghost icon={<SaveOutlined />}>
						Lưu
					</Button>
				</Flex>
			}>
			<Spin spinning={isLoadingMutations} tip="Đang tải...">
				<Form<InvoiceNameForm> form={form} layout="vertical" onFinish={handleOnFinish} initialValues={{ isActive: true }}>
					<Row gutter={[16, 0]}>
						<Col span={24}>
							<Form.Item label="Mã sản phẩm" name="itemCode" rules={[{ required: true, message: "Nhập Mã sản phẩm" }]}>
								<Input placeholder="Nhập mã sản phẩm" />
							</Form.Item>
						</Col>
						<Col span={24}>
							<Form.Item label="Tên sản phẩm" name="itemName" rules={[{ required: true, message: "Nhập tên sản phẩm" }]}>
								<Input placeholder="Nhập tên sản phẩm" />
							</Form.Item>
						</Col>
						<Col span={24}>
							<Form.Item label="Loại gà" name="chickenType" rules={[{ required: true, message: "Chọn loại gà" }]}>
								<Select
									style={{ width: "100%" }}
									options={Array(4)
										.fill(99)
										.map((_, index) => ({ label: (index + 1).toString(), value: index + 1 }))}
									allowClear
									placeholder="Chọn loại gà"
								/>
							</Form.Item>
						</Col>
						<Col span={24}>
							<Form.Item label="Giới tính" name="gender" rules={[{ required: true, message: "Chọn giới tính" }]}>
								<Select style={{ width: "100%" }} options={Object.keys(GenderChicken).map((gender) => ({ label: gender, value: gender }))} allowClear placeholder="Chọn giới tính" />
							</Form.Item>
						</Col>
						<Col span={24}>
							<Form.Item label="Tên xuất hoá đơn" name="invoiceName" rules={[{ required: true, message: "Nhập tên xuất hoá đơn" }]}>
								<Input placeholder="Nhập tên xuất hoá đơn" />
							</Form.Item>
						</Col>
						<Col span={24}>
							<Form.Item label="Tên trên PXK" name="deliveryNoteName">
								<Input placeholder="Nhập tên trên phiếu xuất kho" />
							</Form.Item>
						</Col>
						<Col span={24}>
							<Form.Item label="Trạng thái" name="isActive" valuePropName="checked">
								<Switch checkedChildren="Hoạt động" unCheckedChildren="Không hoạt động" />
							</Form.Item>
						</Col>
					</Row>
				</Form>
			</Spin>
		</Drawer>
	);
};

export default InvoiceNameModal;
